import { supabase } from '../supabase';

export interface ClaimCreateData {
  exporter_id: number;
  receiver: string;
  season: string;
  notes?: string;
  description?: string;
  total_claim_usd?: number;
  currency?: string;
  date_grouping_type?: string | null;
  comparison_type?: string;
  sinister_filter?: string;
  sinister_filter_comparison?: string;
  shipment_type?: string;
  incoterm?: string;
  comparison_clients?: string[];
  product_definition?: string;
  comparison_target?: string;
  filtered_clients?: string[];
  filtered_destination_countries?: string[];
  filtered_varieties?: string[];
  filtered_calibers?: string[];
  filtered_ship_trips?: string[];
  market_filter?: string[];
  include_revenues_main?: boolean;
  include_revenues_comparison?: boolean;
  include_market_in_air?: boolean;
  pool_methodology?: string | null;
  market_reference_date?: string | null;
  exchange_rate_used?: number | null;
  main_references?: number[];
  comparison_references?: number[];
}

export interface ClaimLineCreateData {
  claim_id: number;
  settlement_detail_id: number;
  reason_code: string;
  description: string;
  amount_claim_usd: number;
  status: string;
}

export interface ClaimDetailCreateData {
  claim_id: number;
  settlement_detail_id: number;
  comparable_fob_per_kg: number;
  comparable_kilos: number;
  own_value_per_kg?: number;
}

export async function createClaim(claimData: ClaimCreateData) {
  try {
    const { data, error } = await supabase
      .from('claims')
      .insert({
        ...claimData,
        status: 'draft',
      })
      .select('*')
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating claim:', error);
    throw error;
  }
}



export async function getClaimById(id: number) {
  try {
    console.log("here")
    const { data, error } = await supabase
      .from('claims')
      .select(
        `
        *,
        claims_details!claims_details_claim_id_fkey(*, 
          settlements_details!claims_details_settlement_detail_id_fkey(*,
            settlements(*,
              ship_trips(
                *,
                ships(*)
              )
            )
          )
        )
      `
      )
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error getting claim:', error);
    return null;
  }
}

export async function getClaimsByExporterId(exporterId: number) {
  try {
    const { data, error } = await supabase
      .from('claims')
      .select(
        `
        *,
    claims_details!claims_details_claim_id_fkey(*)
      `
      )
      .eq('exporter_id', exporterId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error getting claims:', error);
    return [];
  }
}

export async function updateClaimStatus(claimId: number, status: string) {
  try {
    const { data, error } = await supabase
      .from('claims')
      .update({
        status: status,
        submitted_at: status === 'submitted' ? new Date().toISOString() : undefined,
      })
      .eq('id', claimId)
      .select('*')
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating claim status:', error);
    throw error;
  }
} 

export async function updateClaimStatusAndAmount(claimId: number, status: string, acceptedAmountUsd: number) {
  try {
    const { data, error } = await supabase
      .from('claims')
      .update({
        status: status,
        total_accepted_usd: acceptedAmountUsd,
        submitted_at: status === 'submitted' ? new Date().toISOString() : undefined,
      })
      .eq('id', claimId)
      .select('*') 
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating claim status and amount:', error);
    throw error;
  }
}

// ADD ↓↓↓
export async function createClaimProducts(
  rows: { claim_id:number; product_key:string; product_definition:string;
          amount_claim_usd:number; status:'pending'; snapshot:any }[]
){
  const { data, error } = await supabase.from('claim_products').insert(rows).select('*');
  if (error) throw error;
  return data;
}

export async function createClaimAllocations(
  rows: { claim_product_id:number; settlement_detail_id:number; portion_usd:number }[]
){
  const { data, error } = await supabase
      .from('claim_settlement_allocations').insert(rows).select('*');
  if (error) throw error;
  return data;
}

export async function distributeClaimStatus(claimId: number) {
  const { data, error } = await supabase
    .from('claims')
    .update({
      status: 'distributed',
      distributed_at: new Date().toISOString(),
    })
    .eq('id', claimId)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

export async function undistributeClaimStatus(claimId: number) {
  const { data, error } = await supabase
    .from('claims')
    .update({
      status: 'accepted',
      distributed_at: null,
    })
    .eq('id', claimId)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

export async function deleteClaim(claimId: number) {
  // Delete claim details first, then the claim itself
  const { error: detailsError } = await supabase
    .from('claims_details')
    .delete()
    .eq('claim_id', claimId);
  if (detailsError) throw detailsError;

  const { error } = await supabase
    .from('claims')
    .delete()
    .eq('id', claimId);
  if (error) throw error;
}

export async function createClaimDetails(details: ClaimDetailCreateData[]) {
  try {
    const { data, error } = await supabase
      .from('claims_details')
      .insert(details)
      .select('*');

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating claim details:', error);
    throw error;
  }
}
